import { useState } from 'react';
import { Button, Card } from '@lms/ui';
import { X, FolderGit2 } from 'lucide-react';
import { useCreateStage, useUpdateStage } from '../hooks/useProgramStages';
import type { ProgramStage } from '@lms/shared/types';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';

interface StageModalProps {
  programId: string;
  levelId: string;
  levelName?: string;
  order: number;
  stage?: ProgramStage;
  onClose: () => void;
}

export function StageModal({
  programId,
  levelId,
  levelName,
  order,
  stage,
  onClose,
}: StageModalProps) {
  const { t } = useLanguage();
  const [stageCode, setStageCode] = useState(stage?.stageCode || '');
  const [name, setName] = useState(stage?.name || '');
  const [description, setDescription] = useState(stage?.description || '');
  const [isSequential, setIsSequential] = useState(stage?.isSequential || false);
  const [passingScore, setPassingScore] = useState(stage?.passingScore ?? 70);

  const createStageMutation = useCreateStage();
  const updateStageMutation = useUpdateStage();

  const isEditing = !!stage;
  const isPending = createStageMutation.isPending || updateStageMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    if (isEditing) {
      updateStageMutation.mutate(
        {
          id: stage.id,
          programId,
          data: {
            stageCode: stageCode.trim() || undefined,
            name: name.trim(),
            description: description.trim() || undefined,
            isSequential,
            passingScore: Number(passingScore) || 70,
            order,
          },
        },
        {
          onSuccess: () => onClose(),
        }
      );
    } else {
      createStageMutation.mutate(
        {
          programId,
          data: {
            levelId,
            stageCode: stageCode.trim() || undefined,
            name: name.trim(),
            description: description.trim() || undefined,
            isSequential,
            passingScore: Number(passingScore) || 70,
            order,
          },
        },
        {
          onSuccess: () => onClose(),
        }
      );
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-center p-4 bg-on-surface/20 backdrop-blur-sm animate-fadeIn overflow-y-auto py-12">
      <Card className="w-full max-w-md !p-0 overflow-hidden shadow-level5 border border-on-surface/5 animate-scaleIn my-auto">
        <div className="p-6 border-b border-outline-variant/10 flex justify-between items-center bg-surface-container-low">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <FolderGit2 size={20} />
            </div>
            <div>
              <h3 className="text-lg font-headline font-bold text-on-surface m-0">
                <Bilingual
                  {...(isEditing ? copy.stageModal.titleEdit : copy.stageModal.titleCreate)}
                />
              </h3>
              <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60">
                {t(
                  isEditing ? copy.stageModal.subtitleEdit : copy.stageModal.subtitleCreate
                ).replace('{levelName}', levelName || '')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-container-high rounded-full transition-colors text-on-surface-variant"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="stage-code"
                className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
              >
                <Bilingual {...copy.stageModal.labelStageCode} />
              </label>
              <input
                id="stage-code"
                type="text"
                value={stageCode}
                onChange={e => setStageCode(e.target.value)}
                placeholder={t(copy.stageModal.placeholderStageCode)}
                className="w-full px-4 py-3 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-sm font-bold text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="stage-name"
                className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
              >
                <Bilingual {...copy.stageModal.labelName} /> <span className="text-error">*</span>
              </label>
              <input
                id="stage-name"
                type="text"
                required
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t(copy.stageModal.placeholderName)}
                className="w-full px-4 py-3 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-sm font-bold text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="stage-desc"
                className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
              >
                <Bilingual {...copy.stageModal.labelDescription} />
              </label>
              <textarea
                id="stage-desc"
                rows={3}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={t(copy.stageModal.placeholderDescription)}
                className="w-full px-4 py-3 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-sm font-medium text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all resize-none"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="stage-passing-score"
                className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
              >
                <Bilingual {...copy.stageModal.labelPassingScore} />
              </label>
              <div className="relative">
                <input
                  id="stage-passing-score"
                  type="number"
                  min="0"
                  max="100"
                  value={passingScore}
                  onChange={e => setPassingScore(Number(e.target.value))}
                  placeholder="70"
                  className="w-full px-4 py-3 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-sm font-bold text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-on-surface-variant/60">
                  %
                </span>
              </div>
              <p className="text-[10px] text-on-surface-variant/70 ml-1">
                <Bilingual {...copy.stageModal.descPassingScore} />
              </p>
            </div>

            <div className="flex items-center gap-3 p-3 bg-surface-container-low rounded-xl">
              <input
                type="checkbox"
                id="stage-sequential"
                checked={isSequential}
                onChange={e => setIsSequential(e.target.checked)}
                className="rounded border-outline-variant text-primary focus:ring-primary h-4 w-4"
              />
              <label
                htmlFor="stage-sequential"
                className="text-xs font-semibold text-on-surface cursor-pointer select-none"
              >
                <span className="block font-bold">
                  <Bilingual {...copy.stageModal.labelSequential} />
                </span>
                <span className="text-[10px] text-on-surface-variant font-normal">
                  <Bilingual {...copy.stageModal.descSequential} />
                </span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-outline-variant/10">
            <Button variant="ghost" type="button" onClick={onClose} disabled={isPending}>
              <Bilingual {...copy.levelModal.abort} />
            </Button>
            <Button variant="primary" type="submit" disabled={isPending || !name.trim()}>
              {isPending ? (
                <Bilingual {...copy.stageModal.btnSaving} />
              ) : isEditing ? (
                <Bilingual {...copy.stageModal.btnSubmitEdit} />
              ) : (
                <Bilingual {...copy.stageModal.btnSubmitCreate} />
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
